<template>
  <v-dialog :value="value" max-width="620" persistent @input="$emit('input', $event)">
    <v-card>
      <v-card-title class="text-h6">
        {{ isEdit ? $t('classlink.form.edit_title') : $t('classlink.form.create_title') }}
      </v-card-title>

      <v-card-text>
        <v-form ref="form" v-model="valid">
          <v-text-field
            v-model="form.displayName"
            :label="$t('classlink.form.display_name')"
            :rules="[requiredRule]"
            counter="255"
            maxlength="255"
          />

          <v-text-field
            v-model="form.tenantKey"
            :label="$t('classlink.form.tenant_key')"
            :rules="isEdit ? [] : [requiredRule, tenantKeyRule]"
            :readonly="isEdit"
            :hint="isEdit
              ? $t('classlink.form.tenant_key_readonly')
              : $t('classlink.form.tenant_key_hint')"
            persistent-hint
            counter="191"
            maxlength="191"
            @input="syncEndpointUrl"
          />

          <v-text-field
            v-model="form.endpointUrl"
            :label="$t('classlink.form.endpoint_url')"
            :rules="[requiredRule, urlRule]"
            :hint="$t('classlink.form.endpoint_url_hint')"
            persistent-hint
            class="mt-4"
            @input="onEndpointUrlInput"
          />

          <v-text-field
            v-model="form.clientId"
            :label="$t('classlink.form.client_id')"
            :rules="[requiredRule]"
            class="mt-4"
            @input="onCredentialInput"
          />

          <v-text-field
            v-model="form.clientSecret"
            :label="$t('classlink.form.client_secret')"
            :rules="isEdit ? [] : [requiredRule]"
            :placeholder="secretPlaceholder"
            :persistent-placeholder="isEdit"
            type="password"
            autocomplete="new-password"
            @input="onCredentialInput"
          />

          <v-textarea
            v-model="form.notes"
            :label="$t('classlink.form.notes')"
            rows="2"
            auto-grow
            counter="5000"
            maxlength="5000"
          />
        </v-form>

        <div class="d-flex align-center mt-2">
          <v-btn
            text
            small
            color="deep-purple"
            :loading="testing"
            @click="testConnection"
          >
            <v-icon left small>mdi-lan-connect</v-icon>
            {{ $t('classlink.form.test_connection') }}
          </v-btn>
          <span class="text-caption grey--text ml-3">{{ $t('classlink.form.test_hint') }}</span>
        </div>

        <v-alert
          v-if="testResult"
          :type="testResult.ok ? 'success' : 'error'"
          dense
          text
          class="mt-3 mb-0"
        >
          {{ testResult.message }}
        </v-alert>
      </v-card-text>

      <v-card-actions>
        <v-spacer />
        <v-btn text :disabled="saving" @click="close">
          {{ $t('classlink.form.cancel') }}
        </v-btn>
        <v-btn color="deep-purple" dark :loading="saving" @click="save">
          {{ $t('classlink.form.save') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import rosterTenantsService from '@/services/roster-tenants.service';
import { apiErrorMessage, defaultEndpointUrl } from '@/utils/classlinkTenants';
import snackbarMixin from '@/mixins/snackbar';

const TENANT_KEY_PATTERN = /^[a-z0-9-]+$/;

const emptyForm = () => ({
  displayName: '',
  tenantKey: '',
  endpointUrl: '',
  clientId: '',
  clientSecret: '',
  notes: '',
});

export default {
  name: 'ClassLinkTenantFormModal',
  mixins: [snackbarMixin],
  props: {
    value: { type: Boolean, default: false },
    tenant: { type: Object, default: null },
  },
  data: () => ({
    form: emptyForm(),
    valid: false,
    saving: false,
    testing: false,
    testResult: null,
    // Una vez que el usuario edita el endpoint a mano, dejamos de derivarlo
    // del tenant key: si no, tipear en el tenant key le pisaría el valor.
    endpointTouched: false,
  }),
  computed: {
    isEdit() {
      return Boolean(this.tenant);
    },
    secretPlaceholder() {
      if (!this.isEdit || !this.tenant.clientSecretHint) {
        return '';
      }
      // `clientSecretHint` ya viene con los puntos delante ('····abcd').
      return this.$t('classlink.form.client_secret_placeholder', { hint: this.tenant.clientSecretHint });
    },
  },
  watch: {
    value(open) {
      if (open) this.reset();
    },
  },
  methods: {
    reset() {
      this.testResult = null;
      this.endpointTouched = Boolean(this.tenant);
      this.form = this.tenant
        ? {
          displayName: this.tenant.displayName,
          tenantKey: this.tenant.tenantKey,
          endpointUrl: this.tenant.endpointUrl,
          clientId: this.tenant.clientId,
          clientSecret: '',
          notes: this.tenant.notes || '',
        }
        : emptyForm();
      this.$nextTick(() => {
        if (this.$refs.form) this.$refs.form.resetValidation();
      });
    },
    requiredRule(value) {
      return Boolean(value) || this.$t('classlink.form.required');
    },
    tenantKeyRule(value) {
      return TENANT_KEY_PATTERN.test(value || '') || this.$t('classlink.form.tenant_key_invalid');
    },
    urlRule(value) {
      return /^https:\/\/\S+$/.test(value || '') || this.$t('classlink.form.url_invalid');
    },
    syncEndpointUrl(tenantKey) {
      if (this.isEdit || this.endpointTouched) return;
      this.form.endpointUrl = tenantKey ? defaultEndpointUrl(tenantKey) : '';
    },
    onEndpointUrlInput() {
      this.endpointTouched = true;
      this.onCredentialInput();
    },
    // El resultado de "Conexión correcta" queda obsoleto en cuanto se toca
    // cualquier credencial (endpoint, client id o secret): sin esto, el
    // alert de éxito sobrevive a una edición que ya no probó nada.
    onCredentialInput() {
      this.testResult = null;
    },
    // En la edición sin secret nuevo se prueba con el guardado (tenantId);
    // si el usuario escribió uno, o si es un alta, se prueban las credenciales
    // del formulario sin guardar nada.
    testPayload() {
      if (this.isEdit && !this.form.clientSecret) {
        return { tenantId: this.tenant.id };
      }
      if (!this.form.endpointUrl || !this.form.clientId || !this.form.clientSecret) {
        return null;
      }
      return {
        endpointUrl: this.form.endpointUrl,
        clientId: this.form.clientId,
        clientSecret: this.form.clientSecret,
      };
    },
    async testConnection() {
      const payload = this.testPayload();
      if (!payload) {
        this.testResult = { ok: false, message: this.$t('classlink.form.test_missing_fields') };
        return;
      }

      this.testing = true;
      this.testResult = null;
      try {
        const result = await rosterTenantsService.testConnection(payload);
        if (result.ok) {
          this.testResult = {
            ok: true,
            message: result.orgName
              ? this.$t('classlink.form.test_ok', { orgName: result.orgName })
              : this.$t('classlink.form.test_ok_no_org'),
          };
        } else {
          this.testResult = { ok: false, message: result.error || this.$t('general.error_msg') };
        }
      } catch (error) {
        this.testResult = { ok: false, message: apiErrorMessage(error, this.$t('general.error_msg')) };
      } finally {
        this.testing = false;
      }
    },
    createPayload() {
      return {
        tenantKey: this.form.tenantKey.trim(),
        displayName: this.form.displayName.trim(),
        endpointUrl: this.form.endpointUrl.trim(),
        clientId: this.form.clientId.trim(),
        clientSecret: this.form.clientSecret,
        notes: this.form.notes ? this.form.notes : null,
      };
    },
    updatePayload() {
      return {
        displayName: this.form.displayName.trim(),
        endpointUrl: this.form.endpointUrl.trim(),
        clientId: this.form.clientId.trim(),
        // Vacío = "no cambiar"; el servicio lo saca del PATCH.
        clientSecret: this.form.clientSecret,
        notes: this.form.notes ? this.form.notes : null,
      };
    },
    async save() {
      if (!this.$refs.form.validate()) return;
      this.saving = true;
      try {
        if (this.isEdit) {
          await rosterTenantsService.update(this.tenant.id, this.updatePayload());
          this.snackbarSuccess(this.$t('classlink.form.updated'));
        } else {
          await rosterTenantsService.create(this.createPayload());
          this.snackbarSuccess(this.$t('classlink.form.created'));
        }
        this.$emit('saved');
        this.close();
      } catch (error) {
        // 409 = ya existe una conexión activa con ese tenant key.
        // 422 = habilitada sin secret. Ambos traen mensaje del backend.
        this.snackbarError(apiErrorMessage(error, this.$t('general.error_msg')));
      } finally {
        this.saving = false;
      }
    },
    close() {
      this.$emit('input', false);
    },
  },
};
</script>
